import React, { Fragment, useEffect, useRef } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface DropdownProps {
  selectedOption: { value: string, label: string, dropdown_label?: string } | null;
  options: Array<{ label: string; value: string, dropdown_label?: string }>;
  onChange: (option: { value: string, label: string, dropdown_label?: string }) => void;
  size: string;
  searchQuery?: string;
  setSearchQuery?: (query: string) => void
}

const sizeToPixels = (size: string) => {
  return size === 'small'
    ? '200px'
    : size === 'medium' ? '240px'
    : '300px'
}

const Dropdown: React.FC<DropdownProps> = ({ searchQuery, setSearchQuery, selectedOption, options, onChange, size='medium' }) => {
  const fixedWidth = sizeToPixels(size);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchQuery]);

  const handleChange = (value: string) => {
    const option = options.find(opt => opt.value === value);
    if (option) {
      onChange(option);
    }
  };

  const filteredOptions = !searchQuery ? options : options.filter(option =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const dropdownLabel = selectedOption?.dropdown_label || 'Category'

  return (
    <div style={{ minWidth: fixedWidth }} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {dropdownLabel}
      </label>
      <Listbox as="div" style={{ minWidth: fixedWidth }} className="relative" value={selectedOption?.value} onChange={handleChange}>
        {({ open }) => (
          <>
            <Listbox.Button
              style={{ width: fixedWidth }}
              className={`relative cursor-pointer rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 py-2 pl-3 pr-10 text-left shadow-lg focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-300 sm:text-sm text-white`}
            >
              <span className="block truncate">
                {selectedOption ? selectedOption.label : 'Select'}
              </span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                {open ? (
                  <FaChevronUp className="h-4 w-4 text-white" aria-hidden="true" />
                ) : (
                  <FaChevronDown className="h-4 w-4 text-white" aria-hidden="true" />
                )}
              </span>
            </Listbox.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-150"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Listbox.Options
                style={{
                  width: fixedWidth,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  overflowX: 'hidden', // Prevent horizontal scroll
                }}
                className="absolute z-10 mt-1 rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              >
                {setSearchQuery &&
                  <div className="px-3 py-2">
                    <input
                      ref={inputRef}
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full border border-gray-300 rounded-md py-1 px-2"
                    />
                  </div>}
                {filteredOptions.map((option, idx) => (
                  <Listbox.Option
                    key={idx}
                    style={{ width: fixedWidth, overflowX: 'hidden' }}
                    className={({ active, selected }) =>
                      `relative cursor-pointer select-none py-2 pl-6 pr-6 ${
                        idx === filteredOptions.length - 1 ? '' : 'border-b border-gray-200'
                      } ${
                        selected
                          ? (active ? 'bg-indigo-500 text-white' : 'bg-indigo-100 text-indigo-900')
                          : 'text-gray-900 hover:bg-indigo-500 hover:text-white'
                      }`
                    }
                    value={option.value}
                  >
                    {() => (
                      <>
                        <span className="block truncate">
                          {option.label}
                        </span>
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  );
};

export default Dropdown;
